import { components } from "@/api-schema";
import { UserStatus } from "@/api-schema-enums";
import { DeviceType } from "@/common/hooks/use-device";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { ImplementedAccessorColumnDef, ImplementedDisplayColumnDef } from "@/components/data-table/types";
import { Button, ButtonVariant } from "@/components/ui/button";
import { UserAvatar } from "@/components/user-avatar/user-avatar";
import { renderUserType } from "@/modules/masterdata/user/_helpers/type-renderer";
import i18next from "i18next";
import { MailIcon, PhoneIcon, SmartphoneIcon } from "lucide-react";
import { BranchWithAddress } from "./_components/branch-with-address";
import { commonColumns } from "./_constants/common-columns";

type UserTableType = components["schemas"]["User"];
type UserType = components["schemas"]["User"]["type"];
type Branch = components["schemas"]["Branch"];

interface ContactInfo {
  email: string | null | undefined;
  telephone: string | null | undefined;
  mobile: string | null | undefined;
}

interface DepartmentInfo {
  departments: UserTableType["departments"];
  occupations: UserTableType["occupations"];
  specialization: UserTableType["specialization"];
}

export const userTableDefaultColumns = {
  columnAvatar: {
    id: "avatar",
    exportPaths: [],
    size: 50,
    enableSorting: false,
    visibility: [DeviceType.Mobile, DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    cell: ({ row }) => {
      const isDisabled = isUserDisabled(row.original);
      return (
        <div className="relative">
          <div className={getDisabledAvatarClassName(isDisabled)}>
            <UserAvatar userAzureObjectUuid={row.original.azureObjectUuid} name={row.getValue("name")} />
          </div>
        </div>
      );
    },
  } as ImplementedDisplayColumnDef<UserTableType>,
  columnName: {
    id: "name",
    exportPaths: ["lastName", "firstName"],
    size: 1000,
    enableSorting: true,
    visibility: [DeviceType.Mobile, DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    header: () => i18next.t("common:Name"),
    accessorFn: (row) => `${row.lastName || ""}, ${row.firstName || ""}`.trim(),
    cell: (row) => {
      const isDisabled = isUserDisabled(row.row.original);
      return <div className={getDisabledClassName(isDisabled)}>{automaticCellRenderer(row.getValue())}</div>;
    },
  } as ImplementedDisplayColumnDef<UserTableType>,
  columnType: {
    id: "type",
    size: 100,
    enableSorting: true,
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    header: () => i18next.t("masterdata:UserType"),
    accessorFn: (row: UserTableType) => row.type,
    cell: (row) => {
      const isDisabled = isUserDisabled(row.row.original);
      return (
        <div className={getDisabledClassName(isDisabled)}>
          {automaticCellRenderer(renderUserType(row.getValue() as UserType))}
        </div>
      );
    },
  } as ImplementedDisplayColumnDef<UserTableType, UserType>,
  columnBranch: {
    id: "branch",
    exportPaths: ["mainBranch.name"],
    size: 500,
    header: () => i18next.t("common:Branch"),
    enableSorting: false,
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => row.mainBranch,
    cell: (row) => (
      <BranchWithAddress
        branch={row.getValue()}
        address={row.row.original.address}
        className={`${isUserDisabled(row.row.original) ? "opacity-40" : ""} whitespace-nowrap`}
      />
    ),
  } as ImplementedAccessorColumnDef<UserTableType, Branch>,
  columnContact: {
    id: "contact",
    exportPaths: ["email", "telephone", "mobile"],
    size: 500,
    header: () => i18next.t("common:Contact"),
    enableSorting: false,
    visibility: [DeviceType.Mobile, DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => ({
      email: row.email,
      telephone: row.telephone,
      mobile: row.mobile,
    }),
    cell: (row) => {
      const contact = row.getValue();
      const isDisabled = isUserDisabled(row.row.original);
      return (
        <div className={`flex flex-col gap-2 whitespace-nowrap ${getDisabledClassName(isDisabled)}`}>
          {contact.email && (
            <Button
              variant={ButtonVariant.Link}
              className="flex h-auto items-center justify-start p-0"
              disabled={isDisabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `mailto:${contact.email}`;
              }}
            >
              <MailIcon className="inline-block h-4 w-4" />
              {contact.email}
            </Button>
          )}
          {contact.telephone && (
            <Button
              variant={ButtonVariant.Link}
              className="flex h-auto items-center justify-start p-0"
              disabled={isDisabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `tel:${contact.telephone}`;
              }}
            >
              <PhoneIcon className="inline-block h-4 w-4" />
              {contact.telephone}
            </Button>
          )}
          {contact.mobile && (
            <Button
              variant={ButtonVariant.Link}
              className="flex h-auto items-center justify-start p-0"
              disabled={isDisabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `tel:${contact.mobile}`;
              }}
            >
              <SmartphoneIcon className="inline-block h-4 w-4" />
              {contact.mobile}
            </Button>
          )}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<UserTableType, ContactInfo>,
  columnDepartment: {
    id: "department",
    exportPaths: ["departments.name", "occupations.name", "specialization"],
    size: 500,
    header: () => i18next.t("common:DepartmentAndFunction"),
    enableSorting: false,
    visibility: [DeviceType.Widescreen],
    accessorFn: (row) => ({
      departments: row.departments,
      occupations: row.occupations,
      specialization: row.specialization,
    }),
    cell: (row) => {
      const value = row.getValue();
      const isDisabled = isUserDisabled(row.row.original);
      const department = value.departments?.at(0) || null;
      const occupation = value.occupations?.at(0) || null;
      const specialization = value.specialization || null;
      return (
        <div className={`flex flex-col gap-2 text-left ${getDisabledClassName(isDisabled)}`}>
          {department && <div className="font-semibold">{department.name}</div>}
          {occupation && <div className="text-xs">{occupation.name}</div>}
          {specialization && <div className="text-xs">{specialization}</div>}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<UserTableType, DepartmentInfo>,
  columnUuid: commonColumns.columnUuid<UserTableType>(),
  columnCreatedAt: commonColumns.columnCreatedAt<UserTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<UserTableType>(),
};

const isUserDisabled = (user: UserTableType): boolean => user.status === UserStatus.Disabled;

const getDisabledAvatarClassName = (isDisabled: boolean): string => (isDisabled ? "opacity-40 grayscale" : "");

const getDisabledClassName = (isDisabled: boolean): string => (isDisabled ? "opacity-40" : "");
